import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import {
  Sparkles, Loader2, Send, ChevronDown, ChevronUp, X,
  MessageSquare, RefreshCw, AlertCircle, CheckCircle2,
  TrendingUp, TrendingDown, Minus
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AGENT_META = {
  vc_partner:       { emoji: '💼', color: 'blue'   },
  founder_in_space: { emoji: '🚀', color: 'purple' },
  growth_expert:    { emoji: '📈', color: 'green'  },
  technical_expert: { emoji: '⚙️',  color: 'cyan'   },
  market_analyst:   { emoji: '📊', color: 'amber'  },
  financial_analyst:{ emoji: '💰', color: 'emerald'},
  customer_champion:{ emoji: '👤', color: 'rose'   },
  risk_assessor:    { emoji: '🛡️',  color: 'red'    },
  deal_structurer:  { emoji: '📝', color: 'indigo' },
  exit_strategist:  { emoji: '🏁', color: 'violet' },
  talent_evaluator: { emoji: '👥', color: 'teal'   },
  competitive_intel:{ emoji: '🎯', color: 'orange' },
};

// Ordering matches AGENT_CONFIGS in the backend
const AGENT_ORDER = Object.keys(AGENT_META);

const COLOR_CLASSES = {
  blue:    { border: 'border-blue-400/30',    badge: 'bg-blue-400/10 text-blue-300',    dot: 'bg-blue-400'    },
  purple:  { border: 'border-purple-400/30',  badge: 'bg-purple-400/10 text-purple-300',dot: 'bg-purple-400'  },
  green:   { border: 'border-green-400/30',   badge: 'bg-green-400/10 text-green-300',  dot: 'bg-green-400'   },
  cyan:    { border: 'border-cyan-400/30',     badge: 'bg-cyan-400/10 text-cyan-300',    dot: 'bg-cyan-400'    },
  amber:   { border: 'border-amber-400/30',   badge: 'bg-amber-400/10 text-amber-300',  dot: 'bg-amber-400'   },
  emerald: { border: 'border-emerald-400/30', badge: 'bg-emerald-400/10 text-emerald-300', dot: 'bg-emerald-400'},
  rose:    { border: 'border-rose-400/30',    badge: 'bg-rose-400/10 text-rose-300',    dot: 'bg-rose-400'    },
  red:     { border: 'border-red-400/30',     badge: 'bg-red-400/10 text-red-300',      dot: 'bg-red-400'     },
  indigo:  { border: 'border-indigo-400/30',  badge: 'bg-indigo-400/10 text-indigo-300',dot: 'bg-indigo-400'  },
  violet:  { border: 'border-violet-400/30',  badge: 'bg-violet-400/10 text-violet-300',dot: 'bg-violet-400'  },
  teal:    { border: 'border-teal-400/30',    badge: 'bg-teal-400/10 text-teal-300',    dot: 'bg-teal-400'    },
  orange:  { border: 'border-orange-400/30',  badge: 'bg-orange-400/10 text-orange-300',dot: 'bg-orange-400'  },
};

const RECOMMENDATION_META = {
  invest:     { label: 'Invest',      icon: CheckCircle2,   cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' },
  dig_deeper: { label: 'Dig Deeper',  icon: TrendingUp,     cls: 'text-blue-400 bg-blue-400/10 border-blue-400/30' },
  watch:      { label: 'Watch',       icon: Minus,          cls: 'text-amber-400 bg-amber-400/10 border-amber-400/30' },
  pass:       { label: 'Pass',        icon: TrendingDown,   cls: 'text-red-400 bg-red-400/10 border-red-400/30' },
};

function scoreColor(score) {
  if (score >= 75) return 'text-emerald-400';
  if (score >= 60) return 'text-blue-400';
  if (score >= 45) return 'text-amber-400';
  return 'text-red-400';
}

function scoreBg(score) {
  if (score >= 75) return 'bg-emerald-400/10 border-emerald-400/30';
  if (score >= 60) return 'bg-blue-400/10 border-blue-400/30';
  if (score >= 45) return 'bg-amber-400/10 border-amber-400/30';
  return 'bg-red-400/10 border-red-400/30';
}

// ---------------------------------------------------------------------------
// Skeleton / loading card
// ---------------------------------------------------------------------------

function AgentSkeletonCard({ agentType }) {
  const meta = AGENT_META[agentType] || {};
  const color = meta.color || 'blue';
  const cc = COLOR_CLASSES[color] || COLOR_CLASSES.blue;

  return (
    <div className={cn(
      'bg-zinc-900 border rounded-lg p-4 flex flex-col gap-3 animate-pulse',
      cc.border
    )}>
      <div className="flex items-center gap-2">
        <span className="text-xl">{meta.emoji}</span>
        <div className="flex-1">
          <div className="h-3 bg-zinc-700 rounded w-1/2 mb-1" />
          <div className="h-2 bg-zinc-800 rounded w-3/4" />
        </div>
        <div className={cn('w-10 h-10 rounded-full border flex items-center justify-center', cc.border)}>
          <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-2 bg-zinc-800 rounded w-full" />
        <div className="h-2 bg-zinc-800 rounded w-5/6" />
        <div className="h-2 bg-zinc-800 rounded w-4/6" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual agent card
// ---------------------------------------------------------------------------

function AgentCard({ result, startupId, sessionId, isExpanded, onToggleExpand, onOpenChat }) {
  const color = result.color || AGENT_META[result.agent_type]?.color || 'blue';
  const cc = COLOR_CLASSES[color] || COLOR_CLASSES.blue;
  const recMeta = RECOMMENDATION_META[result.recommendation] || RECOMMENDATION_META.dig_deeper;
  const RecIcon = recMeta.icon;

  return (
    <div className={cn(
      'bg-zinc-900 border rounded-lg overflow-hidden transition-all duration-200',
      cc.border,
      isExpanded && 'ring-1 ring-inset ring-white/5'
    )}>
      {/* Card header */}
      <div
        className="p-4 cursor-pointer select-none hover:bg-zinc-800/40 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-start gap-3">
          <span className="text-2xl mt-0.5 flex-shrink-0">{result.emoji || AGENT_META[result.agent_type]?.emoji}</span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="text-zinc-50 font-medium text-sm">{result.name}</span>
              <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-mono border', recMeta.cls)}>
                <RecIcon className="w-2.5 h-2.5 inline mr-1" />
                {recMeta.label}
              </span>
            </div>
            <p className="text-zinc-500 text-xs font-mono">{result.persona_title}</p>
          </div>

          <div className={cn(
            'w-12 h-12 rounded-full border-2 flex items-center justify-center flex-shrink-0',
            scoreBg(result.score), scoreColor(result.score)
          )}>
            <span className="font-mono font-bold text-sm">{result.score}</span>
          </div>
        </div>

        {/* Brief preview (first 200 chars) */}
        <p className={cn(
          'text-zinc-400 text-sm mt-3 leading-relaxed',
          !isExpanded && 'line-clamp-3'
        )}>
          {result.brief}
        </p>

        <div className="flex items-center justify-between mt-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
            className="text-zinc-500 hover:text-zinc-300 text-xs flex items-center gap-1 transition-colors"
          >
            {isExpanded ? (
              <><ChevronUp className="w-3 h-3" /> Show less</>
            ) : (
              <><ChevronDown className="w-3 h-3" /> Show full brief</>
            )}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-zinc-800 pt-4">
          {/* Key Insights */}
          {result.key_insights?.length > 0 && (
            <div>
              <p className="text-zinc-500 text-xs font-mono mb-2 uppercase tracking-wide">Key Insights</p>
              <ul className="space-y-1">
                {result.key_insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <span className={cn('w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0', cc.dot)} />
                    {insight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Risks */}
          {result.key_risks?.length > 0 && (
            <div>
              <p className="text-zinc-500 text-xs font-mono mb-2 uppercase tracking-wide">Key Risks</p>
              <ul className="space-y-1">
                {result.key_risks.map((risk, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                    <AlertCircle className="w-3.5 h-3.5 text-red-400/70 mt-0.5 flex-shrink-0" />
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Key Questions */}
          {result.key_questions?.length > 0 && (
            <div>
              <p className="text-zinc-500 text-xs font-mono mb-2 uppercase tracking-wide">Diligence Questions</p>
              <ul className="space-y-1">
                {result.key_questions.map((q, i) => (
                  <li key={i} className="text-sm text-zinc-400 pl-4 border-l border-zinc-700">
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Chat button */}
          <Button
            onClick={() => onOpenChat(result)}
            variant="outline"
            size="sm"
            className={cn('w-full border-zinc-700 hover:text-zinc-50 mt-2', cc.badge)}
          >
            <MessageSquare className="w-3.5 h-3.5 mr-2" />
            Chat with {result.name}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat slide-over
// ---------------------------------------------------------------------------

function AgentChatPanel({ agent, startupId, sessionId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load history
  useEffect(() => {
    (async () => {
      setLoadingHistory(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/startups/${startupId}/diligence-agents/${agent.agent_type}/chat`,
          { credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          setMessages(data.messages || []);
        }
      } catch (_) { /* non-fatal */ }
      setLoadingHistory(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    })();
  }, [agent.agent_type, startupId, API_URL]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg = { role: 'user', content: text, ts: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const resp = await fetch(
        `${API_URL}/api/startups/${startupId}/diligence-agents/${agent.agent_type}/chat`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text, session_id: sessionId }),
        }
      );
      const data = await resp.json();
      const assistantMsg = {
        role: 'assistant',
        content: data.response || 'No response received.',
        ts: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Failed to send message. Please try again.',
        ts: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const color = agent.color || AGENT_META[agent.agent_type]?.color || 'blue';
  const cc = COLOR_CLASSES[color] || COLOR_CLASSES.blue;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="w-full max-w-lg bg-zinc-950 border-l border-zinc-800 flex flex-col h-full shadow-2xl">
        {/* Header */}
        <div className={cn('flex items-center gap-3 p-4 border-b border-zinc-800', cc.badge.split(' ')[0])}>
          <span className="text-2xl">{agent.emoji || AGENT_META[agent.agent_type]?.emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-zinc-50 font-medium text-sm">{agent.name}</p>
            <p className="text-zinc-400 text-xs font-mono truncate">{agent.persona_title}</p>
          </div>
          <div className={cn('px-3 py-1 rounded-full text-xs font-mono border flex-shrink-0', scoreBg(agent.score), scoreColor(agent.score))}>
            {agent.score}/100
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 transition-colors ml-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Prior brief */}
        {agent.brief && (
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/60">
            <p className="text-zinc-500 text-xs font-mono mb-1 uppercase tracking-wide">Agent Brief</p>
            <p className="text-zinc-400 text-xs leading-relaxed line-clamp-4">{agent.brief}</p>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-600" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-zinc-600 text-sm">Ask {agent.name} anything about this startup.</p>
              <div className="mt-4 space-y-2">
                {[
                  'What concerns you most about this deal?',
                  'What valuation would you find compelling?',
                  'What would change your recommendation?',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => setInput(suggestion)}
                    className="block w-full text-left px-3 py-2 rounded-md text-xs text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors border border-zinc-800"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  'flex',
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {msg.role === 'assistant' && (
                  <span className="text-lg mr-2 mt-0.5 flex-shrink-0">
                    {agent.emoji || AGENT_META[agent.agent_type]?.emoji}
                  </span>
                )}
                <div className={cn(
                  'max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed',
                  msg.role === 'user'
                    ? 'bg-zinc-700 text-zinc-50 rounded-br-none'
                    : 'bg-zinc-800 text-zinc-200 rounded-bl-none'
                )}>
                  {msg.content}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <span className="text-lg mr-2">{agent.emoji}</span>
              <div className="bg-zinc-800 rounded-lg rounded-bl-none px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={`Ask ${agent.name} anything...`}
              rows={2}
              className="flex-1 resize-none bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-50 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 transition-colors"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              size="sm"
              className="bg-white text-zinc-900 hover:bg-zinc-200 self-end"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-zinc-600 text-xs mt-1.5">Enter to send · Shift+Enter for new line</p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary bar
// ---------------------------------------------------------------------------

function DiligenceSummaryBar({ results }) {
  const counts = { invest: 0, dig_deeper: 0, watch: 0, pass: 0 };
  let totalScore = 0;
  results.forEach((r) => {
    counts[r.recommendation] = (counts[r.recommendation] || 0) + 1;
    totalScore += r.score || 0;
  });
  const avgScore = results.length ? Math.round(totalScore / results.length) : 0;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-center gap-6 flex-wrap">
      <div className="flex items-center gap-2">
        <span className={cn('font-mono text-3xl font-bold', scoreColor(avgScore))}>{avgScore}</span>
        <div>
          <p className="text-zinc-400 text-xs">Avg Score</p>
          <p className="text-zinc-600 text-xs font-mono">{results.length} agents</p>
        </div>
      </div>
      <div className="h-8 w-px bg-zinc-800" />
      {Object.entries(RECOMMENDATION_META).map(([key, meta]) => {
        const count = counts[key] || 0;
        if (!count) return null;
        const Icon = meta.icon;
        return (
          <div key={key} className="flex items-center gap-1.5">
            <span className={cn('flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono border', meta.cls)}>
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className="text-zinc-300 font-mono text-sm font-medium">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function DiligenceAgentsPanel({ startupId, startupName }) {
  const [status, setStatus] = useState('idle'); // idle | running | done | error
  const [agentResults, setAgentResults] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [chatAgent, setChatAgent] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const readerRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // ---------------------------------------------------------------------------
  // Load previous session on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!startupId) return;
    (async () => {
      setLoadingPrev(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/startups/${startupId}/diligence-agents/latest`,
          { credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.session?.agent_results?.length > 0) {
            const map = {};
            data.session.agent_results.forEach((r) => { map[r.agent_type] = r; });
            setAgentResults(map);
            setSessionId(data.session.session_id);
            setStatus('done');
          }
        }
      } catch (_) { /* non-fatal */ }
      setLoadingPrev(false);
    })();
  }, [startupId, API_URL]);

  // ---------------------------------------------------------------------------
  // Stream handler
  // ---------------------------------------------------------------------------
  const runDiligence = useCallback(async () => {
    // Abort any existing stream
    if (readerRef.current) {
      try { readerRef.current.cancel(); } catch (_) {}
    }

    setStatus('running');
    setAgentResults({});
    setErrorMsg(null);
    setExpandedAgent(null);
    setSessionId(null);

    try {
      const response = await fetch(
        `${API_URL}/api/startups/${startupId}/diligence-agents/run`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          let event;
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === 'agent_result') {
            const r = event.data;
            setAgentResults(prev => ({ ...prev, [r.agent_type]: r }));
          } else if (event.type === 'agent_error') {
            // Show a placeholder error card
            const agentType = event.agent_type;
            const meta = AGENT_META[agentType] || {};
            setAgentResults(prev => ({
              ...prev,
              [agentType]: {
                agent_type: agentType,
                name: event.name || agentType,
                persona_title: 'Analysis failed',
                emoji: meta.emoji || '❓',
                color: meta.color || 'red',
                brief: `Analysis failed: ${event.error}`,
                score: 0,
                recommendation: 'dig_deeper',
                key_insights: [],
                key_risks: [],
                key_questions: [],
              },
            }));
          } else if (event.type === 'complete') {
            setSessionId(event.session_id);
            setStatus('done');
          }
        }
      }

      // If stream ended without a complete event, mark done
      setStatus(prev => prev === 'running' ? 'done' : prev);

    } catch (err) {
      setErrorMsg(err.message || 'Failed to run diligence agents.');
      setStatus('error');
    }
  }, [startupId, API_URL]);

  // Cleanup reader on unmount
  useEffect(() => {
    return () => {
      if (readerRef.current) {
        try { readerRef.current.cancel(); } catch (_) {}
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const resultsArray = AGENT_ORDER
    .map(at => agentResults[at])
    .filter(Boolean);

  const pendingAgents = AGENT_ORDER.filter(at => !agentResults[at]);

  const isRunning = status === 'running';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loadingPrev) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-zinc-50 font-semibold font-['Outfit'] text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            AI Diligence Panel
          </h2>
          <p className="text-zinc-500 text-sm mt-0.5">
            12 specialist agents analyse {startupName || 'this startup'} from independent perspectives
          </p>
        </div>

        <Button
          onClick={runDiligence}
          disabled={isRunning}
          className="bg-white text-zinc-900 hover:bg-zinc-200"
        >
          {isRunning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running ({resultsArray.length}/12)</>
          ) : status === 'done' ? (
            <><RefreshCw className="w-4 h-4 mr-2" />Re-run Diligence</>
          ) : (
            <><Sparkles className="w-4 h-4 mr-2" />Run Diligence</>
          )}
        </Button>
      </div>

      {/* Error */}
      {status === 'error' && errorMsg && (
        <div className="flex items-start gap-3 rounded-md border border-red-400/20 bg-red-400/10 px-4 py-3">
          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-200">{errorMsg}</p>
        </div>
      )}

      {/* Empty state */}
      {status === 'idle' && resultsArray.length === 0 && (
        <div className="border border-dashed border-zinc-700 rounded-lg p-12 text-center space-y-4">
          <div className="text-5xl">🔬</div>
          <div>
            <p className="text-zinc-300 font-medium">No diligence run yet</p>
            <p className="text-zinc-500 text-sm mt-1">
              Click <strong>Run Diligence</strong> to deploy 12 AI agents that each evaluate this
              startup from a different expert perspective — VC partner, domain founder, growth expert,
              CTO, market analyst, and more.
            </p>
          </div>
          <Button
            onClick={runDiligence}
            className="bg-white text-zinc-900 hover:bg-zinc-200"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Run Diligence
          </Button>
        </div>
      )}

      {/* Summary bar */}
      {resultsArray.length > 0 && (
        <DiligenceSummaryBar results={resultsArray} />
      )}

      {/* Agent grid */}
      {(resultsArray.length > 0 || isRunning) && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Completed agents (in canonical order) */}
          {AGENT_ORDER.filter(at => agentResults[at]).map(agentType => {
            const result = agentResults[agentType];
            return (
              <AgentCard
                key={agentType}
                result={result}
                startupId={startupId}
                sessionId={sessionId}
                isExpanded={expandedAgent === agentType}
                onToggleExpand={() => setExpandedAgent(prev => prev === agentType ? null : agentType)}
                onOpenChat={setChatAgent}
              />
            );
          })}

          {/* Pending skeleton cards (only while running) */}
          {isRunning && pendingAgents.map(agentType => (
            <AgentSkeletonCard key={agentType} agentType={agentType} />
          ))}
        </div>
      )}

      {/* Chat slide-over */}
      {chatAgent && (
        <AgentChatPanel
          agent={chatAgent}
          startupId={startupId}
          sessionId={sessionId}
          onClose={() => setChatAgent(null)}
        />
      )}
    </div>
  );
}
